import { useMemo, useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { nip19 } from 'nostr-tools';

import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useLoginActions } from '@/hooks/useLoginActions';
import { useSearchProfiles, type SearchProfile } from '@/hooks/useSearchProfiles';
import { useDittoThemeContext } from '@/components/DittoThemeProvider';
import { DateDoodle } from '@/components/DateDoodle';
import { IridescentAvatar } from '@/components/IridescentAvatar';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import LoginDialog from '@/components/auth/LoginDialog';
import { LoginArea } from '@/components/auth/LoginArea';
import { getAvatarShape } from '@/lib/avatarShape';
import { genUserName } from '@/lib/genUserName';
import { PASTEL_STRIP_COLORS } from '@/lib/polaroid-utils';
import { isVideoBg } from '@/lib/dittoTheme';
import { cn } from '@/lib/utils';

interface TopHeaderProps {
  /** When true, the header slides up out of view */
  hidden?: boolean;
}

/** Tape strip colors — 8 variants, reference CSS variables so they adapt to active Ditto theme */
const TAPE_COLORS = [
  'var(--tape-0-solid)',
  'var(--tape-1-solid)',
  'var(--tape-2-solid)',
  'var(--tape-3-solid)',
  'var(--tape-4-solid)',
  'var(--tape-5-solid)',
  'var(--tape-6-solid)',
  'var(--tape-7-solid)',
];

export function TopHeader({ hidden = false }: TopHeaderProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, metadata, event: profileEvent } = useCurrentUser();
  const { activeTheme, hasBgImage } = useDittoThemeContext();

  const avatarShape = useMemo(() => {
    if (!profileEvent) return undefined;
    try {
      return getAvatarShape(JSON.parse(profileEvent.content));
    } catch {
      return undefined;
    }
  }, [profileEvent]);
  const { logout } = useLoginActions();
  const [loginOpen, setLoginOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);

  const { data: searchProfiles, isFetching: isSearching } = useSearchProfiles(searchQuery);

  // Reset search when menu closes
  useEffect(() => {
    if (!menuOpen) {
      setSearchQuery('');
    }
  }, [menuOpen]);

  // When a theme is active, use its tape colors; otherwise, cycle the 5 pastel construction paper colors.
  const usePastel = !activeTheme;

  const menuItems = [
    { label: 'Home', path: '/', tapeColor: usePastel ? PASTEL_STRIP_COLORS[0] : TAPE_COLORS[0], doodleSeed: 'menu-home' },
    { label: 'Global', path: '/global', tapeColor: usePastel ? PASTEL_STRIP_COLORS[1] : TAPE_COLORS[3], doodleSeed: 'menu-global' },
    { label: 'Search', path: '/search', tapeColor: usePastel ? PASTEL_STRIP_COLORS[2] : TAPE_COLORS[5], doodleSeed: 'menu-search' },
    ...(user ? [
      { label: 'My Profile', path: `/${nip19.npubEncode(user.pubkey)}`, tapeColor: usePastel ? PASTEL_STRIP_COLORS[3] : TAPE_COLORS[1], doodleSeed: 'menu-profile' },
      { label: 'My Albums', path: '/album', tapeColor: usePastel ? PASTEL_STRIP_COLORS[4] : TAPE_COLORS[6], doodleSeed: 'menu-albums' },
    ] : []),
    { label: 'Theme', path: '/themes', tapeColor: usePastel ? PASTEL_STRIP_COLORS[0] : TAPE_COLORS[2], doodleSeed: 'menu-theme' },
  ];

  const handleNavigate = (path: string) => {
    setMenuOpen(false);
    navigate(path);
  };

  const handleSearchSelect = useCallback((profile: SearchProfile) => {
    const npub = nip19.npubEncode(profile.pubkey);
    setMenuOpen(false);
    setSearchQuery('');
    navigate(`/${npub}`);
  }, [navigate]);

  const handleViewAllResults = useCallback(() => {
    if (!searchQuery.trim()) return;
    const q = searchQuery.trim();
    setMenuOpen(false);
    setSearchQuery('');
    navigate(`/search?q=${encodeURIComponent(q)}`);
  }, [searchQuery, navigate]);

  // Whether the sidebar has a bg image behind it (needs paper-strip on text)
  const sidebarHasBg = !!(activeTheme?.background);

  return (
    <>
      <header
        className="fixed top-0 left-0 right-0 z-50 h-[52px] bg-cream/95 backdrop-blur-sm border-b border-cream-dark/50 transition-transform duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]"
        style={{ transform: hidden ? 'translateY(-100%)' : 'translateY(0)' }}
      >
        <div className="max-w-[480px] mx-auto h-full flex items-center justify-between px-4">
          {/* Left: hand-drawn pastel camera icon */}
          <button
            onClick={() => navigate('/upload')}
            className="w-9 h-9 flex items-center justify-center transition-transform active:scale-90"
            aria-label="Upload photo"
          >
            <svg width="30" height="26" viewBox="0 0 30 26" fill="none" xmlns="http://www.w3.org/2000/svg">
              <defs>
                <linearGradient id="cam-body" x1="0" y1="0" x2="30" y2="26" gradientUnits="userSpaceOnUse">
                  <stop offset="0%" stopColor="#F0B8D0" />
                  <stop offset="30%" stopColor="#D4B8F0" />
                  <stop offset="55%" stopColor="#B8E0F0" />
                  <stop offset="80%" stopColor="#B8F0D8" />
                  <stop offset="100%" stopColor="#F0E8B8" />
                </linearGradient>
              </defs>
              {/* Camera body — sharp corners, wobbly hand-drawn path */}
              <path d="M2 7.5 L28 7 L28.5 24 L1.5 24.5 Z" fill="url(#cam-body)" stroke="#3D3040" strokeWidth="1.8" strokeLinejoin="round" />
              {/* Viewfinder bump — sharp trapezoid */}
              <path d="M10.5 7.5 L11 3.5 L19 3 L19.5 7" fill="url(#cam-body)" stroke="#3D3040" strokeWidth="1.8" strokeLinejoin="round" />
              {/* Flash dot */}
              <circle cx="7" cy="11.5" r="1.3" fill="#3D3040" />
              {/* Lens — slightly wobbly circles */}
              <circle cx="15" cy="16" r="5.8" fill="#E8E4E0" stroke="#3D3040" strokeWidth="1.6" />
              <circle cx="15" cy="16" r="3.8" fill="#4A4050" />
              <circle cx="15" cy="16" r="2" fill="#2D1B2E" />
              {/* Lens highlight */}
              <circle cx="13.2" cy="14.3" r="1.1" fill="rgba(255,255,255,0.55)" />
            </svg>
          </button>

          {/* Center: Logo */}
          <button
            onClick={() => {
              if (location.pathname === '/') {
                window.scrollTo({ top: 0, behavior: 'smooth' });
              } else {
                navigate('/');
              }
            }}
            className="font-logo text-[28px] leading-none select-none tracking-wide transition-transform active:scale-95 logo-outlined"
            data-text="Polaroids"
          >
            <span className="logo-fill">Polaroids</span>
          </button>

          {/* Right: hand-drawn hamburger menu — wobbly pen strokes */}
          <button
            onClick={() => setMenuOpen(true)}
            className="w-9 h-9 flex items-center justify-center transition-transform active:scale-90 text-pol-text"
            aria-label="Open menu"
          >
            <svg width="22" height="18" viewBox="0 0 22 18" fill="none" xmlns="http://www.w3.org/2000/svg">
              {/* Three hand-drawn lines with slight wobble and varying angle */}
              <path d="M1 1.8 C5 1.2, 10 2.2, 15 1.5 C17 1.3, 19 1.9, 21 1.5" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" fill="none" />
              <path d="M0.5 9.2 C4 8.6, 9 9.5, 14 9 C17 8.7, 19.5 9.3, 21.5 8.8" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" fill="none" />
              <path d="M1.2 16.5 C5 16, 10 16.8, 15 16.2 C17.5 16, 19 16.6, 21 16.3" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" fill="none" />
            </svg>
          </button>
        </div>
      </header>

      {/* Side menu */}
      <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
        <SheetContent
          side="right"
          className="w-[270px] p-0 border-0 shadow-none bg-transparent overflow-visible [&>button]:z-50"
        >
          <SheetTitle className="sr-only">Menu</SheetTitle>

          {/*
           * Sidebar surface — shows the theme's background at full opacity.
           * No color overlays, no transparency on the bg image. Paper is opaque.
           */}
          <div
            className="h-full relative overflow-hidden"
            style={{
              backgroundColor: activeTheme?.colors.background ?? 'rgb(var(--pol-bg))',
              boxShadow: '-4px 0 16px rgba(45,27,78,0.12)',
            }}
          >
            {/* Theme background image — FULL opacity, no overlay */}
            {activeTheme?.background && !isVideoBg(activeTheme.background.mime) && (
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  backgroundImage: `url(${activeTheme.background.url})`,
                  backgroundSize: activeTheme.background.mode === 'cover' ? 'cover' : 'auto',
                  backgroundPosition: activeTheme.background.mode === 'cover' ? 'center' : 'top left',
                  backgroundRepeat: activeTheme.background.mode === 'cover' ? 'no-repeat' : 'repeat',
                }}
              />
            )}

            {/* Noise texture overlay for paper feel (on top of bg image) */}
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.03'/%3E%3C/svg%3E\")",
                backgroundRepeat: 'repeat',
                backgroundSize: '256px 256px',
              }}
            />

            {/* Decorative tape at top */}
            <div
              className="absolute top-3 left-1/2 -translate-x-1/2 w-[100px] h-[22px] tape-rainbow z-20"
              style={{ transform: 'translateX(-50%) rotate(-2deg)' }}
            />

            {/* Content layer — sits above bg, fills full height for footer positioning */}
            <div className="relative z-10 h-full flex flex-col">
              {/* User section */}
              <div className="pt-14 pb-5 px-5">
                {user ? (
                  <button
                    onClick={() => handleNavigate(`/${nip19.npubEncode(user.pubkey)}`)}
                    className="flex items-center gap-3 w-full text-left group"
                  >
                    <IridescentAvatar
                      src={metadata?.picture}
                      alt={metadata?.name ?? 'Profile'}
                      size={40}
                      shape={avatarShape}
                      themeColor={activeTheme?.colors.background}
                    />
                    <div className="min-w-0">
                      {/* Name always gets a paper strip for legibility */}
                      <p className={cn(
                        'font-hand text-lg truncate group-hover:text-pol-pink transition-colors leading-tight text-pol-text',
                        sidebarHasBg ? 'paper-strip' : 'paper-strip-pastel',
                      )}>
                        {metadata?.name ?? 'Anonymous'}
                      </p>
                      <p className={cn(
                        'text-[11px] text-pol-text-secondary truncate font-sans',
                        sidebarHasBg ? 'paper-strip' : 'paper-strip-pastel',
                      )}>
                        {metadata?.nip05 ?? `${nip19.npubEncode(user.pubkey).slice(0, 16)}...`}
                      </p>
                    </div>
                  </button>
                ) : (
                  <div className="flex flex-col items-center gap-3 py-2">
                    <div className="w-12 h-12 bg-gradient-to-br from-pol-pink/20 to-pol-lilac/20 border-2 border-dashed border-pol-pink/30 flex items-center justify-center">
                      <span className="font-hand text-2xl text-pol-text-secondary/60">?</span>
                    </div>
                    <LoginArea className="w-full" />
                  </div>
                )}
              </div>

              {/* Divider — dotted line like notebook paper */}
              <div className="mx-5 border-b-2 border-dotted border-pol-text/10" />

              {/* Quick search — hand-drawn search box */}
              <div className="px-4 pt-3 pb-1">
                <div className="relative">
                  {/* Hand-drawn magnifying glass icon */}
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
                    {isSearching ? (
                      <svg className="w-4 h-4 text-pol-text-secondary animate-spin" viewBox="0 0 24 24" fill="none">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                    ) : (
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <circle cx="6.5" cy="6.5" r="4.8" stroke="currentColor" strokeWidth="1.8" className="text-pol-text-secondary" />
                        <path d="M10 10.5 L14.5 14.8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="text-pol-text-secondary" />
                      </svg>
                    )}
                  </div>
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && searchQuery.trim()) {
                        handleViewAllResults();
                      }
                    }}
                    placeholder="Search people..."
                    className="w-full pl-9 pr-3 py-2 bg-polaroid-white text-pol-text font-hand text-base placeholder:text-pol-text-secondary/50 outline-none border-2 border-dashed border-pol-text/15 focus:border-pol-pink/40 transition-colors shadow-[0_1px_4px_rgba(45,27,78,0.06)]"
                    autoComplete="off"
                    autoCorrect="off"
                    spellCheck={false}
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery('')}
                      className="absolute right-2 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center text-pol-text-secondary hover:text-pol-text transition-colors"
                    >
                      <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                        <path d="M1.5 1.5 L8.5 8.5 M8.5 1.5 L1.5 8.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                      </svg>
                    </button>
                  )}
                </div>

                {/* Quick search results — profile list */}
                {searchQuery.trim().length > 0 && searchProfiles && searchProfiles.length > 0 && (
                  <div className="mt-1.5 bg-polaroid-white shadow-[0_2px_8px_rgba(45,27,78,0.1)] border border-pol-text/5 overflow-hidden">
                    {searchProfiles.slice(0, 5).map((profile) => (
                      <SidebarSearchResult
                        key={profile.pubkey}
                        profile={profile}
                        onClick={handleSearchSelect}
                      />
                    ))}
                    {/* View all results link */}
                    <button
                      onClick={handleViewAllResults}
                      className="w-full px-3 py-2 text-left font-hand text-sm text-pol-pink hover:bg-pol-pink/5 transition-colors border-t border-dashed border-pol-text/10"
                    >
                      View all results for "{searchQuery.trim()}" →
                    </button>
                  </div>
                )}

                {/* No results */}
                {searchQuery.trim().length > 0 && !isSearching && searchProfiles && searchProfiles.length === 0 && (
                  <div className="mt-1.5 bg-polaroid-white shadow-[0_2px_8px_rgba(45,27,78,0.1)] border border-pol-text/5 px-3 py-3">
                    <p className="font-hand text-sm text-pol-text-secondary text-center">No people found</p>
                    <button
                      onClick={handleViewAllResults}
                      className="w-full mt-1 font-hand text-sm text-pol-pink hover:underline text-center"
                    >
                      Search posts for "{searchQuery.trim()}" →
                    </button>
                  </div>
                )}
              </div>

              {/* Nav items — opaque paper cards with tape + shadow, active gets doodle */}
              <nav className="px-4 py-4 space-y-2">
                {menuItems.map(({ label, path, tapeColor, doodleSeed }, i) => {
                  const isActive = path === '/'
                    ? location.pathname === '/'
                    : location.pathname.startsWith(path);

                  const rotation = [-1.5, 0.8, -0.5, 1.2][i % 4];

                  return (
                    <button
                      key={label}
                      onClick={() => handleNavigate(path)}
                      className="relative flex items-center gap-2 w-full px-4 py-3 text-left bg-polaroid-white shadow-[0_2px_6px_rgba(45,27,78,0.1)] transition-all active:scale-[0.97] hover:shadow-[0_3px_10px_rgba(45,27,78,0.15)] hover:-translate-y-px"
                      style={{ transform: `rotate(${rotation}deg)` }}
                    >
                      {/* Mini tape on the left edge */}
                      <div
                        className="absolute left-0 top-1/2 -translate-y-1/2 w-[14px] h-[28px]"
                        style={{
                          backgroundColor: tapeColor,
                          opacity: isActive ? 0.85 : 0.55,
                          transform: 'translateX(-4px)',
                        }}
                      />

                      <span className={cn(
                        'font-hand text-xl',
                        isActive ? 'text-pol-text font-bold' : 'text-pol-text font-bold opacity-60',
                      )}>
                        {label}
                      </span>

                      {/* Active doodle — only on the right side */}
                      {isActive && (
                        <span className="flex-shrink-0 ml-auto">
                          <DateDoodle seed={doodleSeed} size={20} />
                        </span>
                      )}
                    </button>
                  );
                })}
              </nav>

              {/* Spacer pushes footer to bottom */}
              <div className="flex-1" />

              {/* Bottom footer */}
              <div className="px-5 py-4 space-y-3">
                <div className="border-t-2 border-dotted border-pol-text/10 pt-3 space-y-3">
                  {/* Logout */}
                  {user && (
                    <button
                      onClick={async () => {
                        setMenuOpen(false);
                        await logout();
                      }}
                      className={cn(
                        'font-hand text-sm text-pol-text-secondary hover:text-pol-text transition-colors block',
                        sidebarHasBg && 'paper-strip',
                      )}
                    >
                      Log out
                    </button>
                  )}

                  {/* Vibed with shakespeare */}
                  <a
                    href="https://shakespeare.diy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={cn(
                      'font-hand text-sm text-pol-text-secondary hover:text-pol-text transition-colors block',
                      sidebarHasBg && 'paper-strip',
                    )}
                  >
                    Vibed with Shakespeare
                  </a>
                </div>
              </div>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      <LoginDialog
        isOpen={loginOpen}
        onClose={() => setLoginOpen(false)}
        onLogin={() => setLoginOpen(false)}
      />
    </>
  );
}

/** A single profile result in the sidebar search dropdown. */
function SidebarSearchResult({ profile, onClick }: { profile: SearchProfile; onClick: (p: SearchProfile) => void }) {
  const { metadata, pubkey } = profile;
  const displayName = metadata.display_name || metadata.name || genUserName(pubkey);
  const nip05 = metadata.nip05;
  const nip05Display = nip05 ? (nip05.startsWith('_@') ? nip05.slice(2) : nip05) : undefined;
  const identifier = nip05Display || `${nip19.npubEncode(pubkey).slice(0, 16)}...`;

  return (
    <button
      onClick={() => onClick(profile)}
      className="w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-pol-pink/5 transition-colors"
    >
      {/* Avatar */}
      <div className="w-8 h-8 rounded-full overflow-hidden bg-cream-dark border border-pol-pink/20 flex-shrink-0">
        {metadata.picture ? (
          <img src={metadata.picture} alt={displayName} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-pol-pink/30 to-pol-lilac/30 flex items-center justify-center">
            <span className="font-hand text-xs text-pol-text-secondary">{displayName[0]?.toUpperCase() || '?'}</span>
          </div>
        )}
      </div>
      {/* Name + identifier */}
      <div className="min-w-0 flex-1">
        <p className="font-hand text-sm text-pol-text truncate leading-tight">{displayName}</p>
        <p className="text-[10px] text-pol-text-secondary truncate leading-tight font-sans">{identifier}</p>
      </div>
    </button>
  );
}
